import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../constants/common'
import { getQaSelector } from '../../../utils/common'
import {
  isRepeatedClick,
  isSubmitKeyDown,
  preventDefault,
} from '../../../utils/events'
import { AIButtonContainedDark, AIButtonContainedLight } from '../../AIButton'
import { FloatingTooltip } from '../../Floating'
import { TextareaElement, useTextareaFocusHandler } from '../../Textarea'
import { useGretaAIChatContext } from '../GretaAIChat.hooks'
import { getIsGretaAIChatThinking } from '../GretaAIChat.utils'
import { GretaAIChatSourceSwitch } from '../GretaAIChatSourceSwitch'

import {
  GRETA_COMPOSER_DOUBLE_CLICK_TIMEOUT_MS,
  GRETA_COMPOSER_MAX_LENGTH,
  GRETA_COMPOSER_MAX_ROWS,
  GRETA_COMPOSER_MIN_ROWS,
  QA_NAME_GRETA_AI_CHAT_COMPOSER,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_INPUT,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_SEND,
  QA_NAME_GRETA_AI_CHAT_COMPOSER_STOP,
} from './GretaAIChatComposer.constants'
import { getGretaAIChatComposerPlaceholderKey } from './GretaAIChatComposer.utils'

import * as styles from './GretaAIChatComposer.module.css'

import StopIcon from '../../../icons/stop.svg'

function GretaAIChatComposer({ inputRef }) {
  const { t } = useTranslation()

  const { actions, conversation, draft, panel, source } =
    useGretaAIChatContext()

  const { isLimitReached, isLocked } = panel

  const { isSendPending, status } = conversation

  const { setDraft, onSendMessage, onStop } = actions

  const {
    canSwitch,
    isFinanceNotBought,
    isPinned,
    isSourceOff,
    isSourceSwitchShown,
    placeholderKey,
    source: selectedSource,
    onSelectSource,
  } = source

  const isGenerating = getIsGretaAIChatThinking(status)

  const { focused, handleBlur, handleFocus } = useTextareaFocusHandler()
  const lastSendAtRef = useRef(0)

  const qaProps = getQaSelector({ value: QA_NAME_GRETA_AI_CHAT_COMPOSER })

  const isSendDisabled =
    isGenerating ||
    isLimitReached ||
    isLocked ||
    isSendPending ||
    isSourceOff ||
    draft.trim().length === 0

  const composerPlaceholderKey = getGretaAIChatComposerPlaceholderKey({
    isLimitReached,
    isLocked,
    placeholderKey,
  })

  function handleSend() {
    if (!isSendDisabled) {
      lastSendAtRef.current = Date.now()
      onSendMessage(draft)
    }
  }

  function handleStop(event) {
    // The second click of a double-click can land on Stop after Send rerenders.
    const shouldIgnoreStop =
      isRepeatedClick(event) ||
      Date.now() - lastSendAtRef.current <
        GRETA_COMPOSER_DOUBLE_CLICK_TIMEOUT_MS

    if (!shouldIgnoreStop) {
      onStop()
    }
  }

  function handleKeyDown(event) {
    if (isSubmitKeyDown(event)) {
      preventDefault(event)
      handleSend()
    }
  }

  const sendTooltip = isSendDisabled
    ? undefined
    : t('Greta.ComposerSendTooltipKey')

  return (
    <div className={styles.root}>
      <FloatingTooltip content={t(composerPlaceholderKey)} disabled={!isLocked}>
        <div {...qaProps} className={styles.composer}>
          <TextareaElement
            autoResizeHeight
            className={styles.input}
            disabled={isLimitReached || isLocked || isSourceOff}
            focused={focused}
            hidePlaceholderOnFocus={false}
            maxLength={GRETA_COMPOSER_MAX_LENGTH}
            maxRows={GRETA_COMPOSER_MAX_ROWS}
            minRows={GRETA_COMPOSER_MIN_ROWS}
            placeholder={t(composerPlaceholderKey)}
            qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_INPUT}
            ref={inputRef}
            value={draft}
            onBlur={handleBlur}
            onChange={setDraft}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
          >
            <div className={styles.actions}>
              {isSourceSwitchShown ? (
                <GretaAIChatSourceSwitch
                  canSwitch={canSwitch}
                  className={styles.sourceSwitch}
                  isFinanceNotBought={isFinanceNotBought}
                  isPinned={isPinned}
                  source={selectedSource}
                  onSelect={onSelectSource}
                />
              ) : null}
              {isGenerating ? (
                <AIButtonContainedLight
                  beforeIconProps={{ Icon: StopIcon }}
                  qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_STOP}
                  text={t('Greta.StopButtonKey')}
                  tooltip={t('Greta.StopTooltipKey')}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  onClick={handleStop}
                />
              ) : (
                <AIButtonContainedDark
                  disabled={isSendDisabled}
                  qaName={QA_NAME_GRETA_AI_CHAT_COMPOSER_SEND}
                  text={t('Greta.SendButtonKey')}
                  tooltip={sendTooltip}
                  tooltipPlacement={FLOATING_ELEMENT_PLACEMENT.top}
                  onClick={handleSend}
                />
              )}
            </div>
          </TextareaElement>
        </div>
      </FloatingTooltip>
      <p className={styles.disclaimer}>{t('Greta.ComposerDisclaimerKey')}</p>
    </div>
  )
}

export default GretaAIChatComposer
